import * as React from "react";

import { getValueOnBreakpoint } from "../../../lib/getValueOnBreakpoint";
import { SETTINGS_GUI } from "../../../utils/settings";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { TUseXElement } from "../../../hooks/useXElement";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import {
  IXGUIFieldBase,
  TOnChange,
  TXGUIField,
} from "../../../utils/interfaces/gui/fields.interfaces";

const XGUITextField = React.lazy(() => import("../components/XGUITextField"));

const XGUIFilePicker = React.lazy(() => import("../components/XGUIFilePicker"));

const XGUISizingField = React.lazy(
  () => import("../components/XGUISizingField")
);

const XGUIColorField = React.lazy(() => import("../components/XGUIColorField"));

const XGUISelectOptionField = React.lazy(
  () => import("../components/XGUISelectOptionField")
);

const XGUIToggleOptionField = React.lazy(
  () => import("../components/XGUIToggleOptionField")
);

const XGUIFontField = React.lazy(() => import("../components/XGUIFontField"));

const XGUIBoxShadowField = React.lazy(
  () => import("../components/XGUIBoxShadowField")
);

const XGUISideField = React.lazy(() => import("../components/XGUISideField"));

const XGUIRadiusField = React.lazy(
  () => import("../components/XGUIRadiusField")
);

const XGUIBorderField = React.lazy(
  () => import("../components/XGUIBorderField")
);

const XGUIDesignTab: React.FC<{
  selectedElement: TUseXElement<any>;
  setSelectedElement: any;
}> = ({ selectedElement, setSelectedElement }) => {
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };
  // WE TRACK THE BREAK POINTS CHANGES
  // TO UPDATE THE ELEMENT RELATIVELY TO THE BREAKPOINT
  const breakpoint = useBreakpoint();

  if (!breakpoint) return null;

  // CHANGE EVENT FOR ALL COMPONENTS
  const onChange: TOnChange<any> = (value, field, _breakpoint = breakpoint) => {
    setSelectedElement((old: any) => {
      const breakPointPaylaod = {
        ...(old.value[_breakpoint] ?? {}),
        [field]: value,
      };

      Object.keys(breakPointPaylaod).forEach((key) => {
        if (
          typeof breakPointPaylaod[key] === "undefined" ||
          breakPointPaylaod[key] === ""
        ) {
          delete breakPointPaylaod[key];
        }
      });

      const payload = {
        ...old,
        value: {
          ...old.value,
          [_breakpoint]: breakPointPaylaod,
        },
      };

      return payload;
    });
  };

  const value = getValueOnBreakpoint(selectedElement.value as any, breakpoint);

  React.useEffect(() => {
    const target = document.getElementById(selectedElement.index);

    if (target) target.setAttribute("data-x-selected", "true");
  }, [selectedElement.value]);

  const settings = SETTINGS_GUI[selectedElement.name] ?? {};

  const titles = Object.keys(settings);

  return (
    <Stack
      sx={{
        ".MuiAccordion-root": {
          borderRadius: 0,
          color: "white",
          bgcolor: "#0E0D18",

          "&.MuiPaper-elevation": {
            boxShadow: "none",
          },

          ".MuiSvgIcon-root": {
            fontSize: 15,
            color: "white",
          },

          ".MuiAccordionSummary-root": {
            px: 0,
          },
        },
        ".MuiToggleButtonGroup-root, .MuiInputBase-root, .MuiFab-root": {
          border: "2px solid #222132",
          minHeight: 30,
        },
        ".MuiToggleButtonGroup-root": {
          bgcolor: "#222132",

          ".MuiToggleButton-root": {
            py: 0,
            fontSize: 9.5,
            color: "white",
            fontWeight: 700,

            "&.Mui-selected": {
              bgcolor: "rgba(0, 0, 0, 0.5)",
              borderRadius: 1,
            },

            "&.Mui-disabled": {
              color: "rgba(0, 0, 0, 0.38)",
              svg: {
                color: "inherit",
              },
            },
          },
        },
        ".MuiButtonBase-root": {
          bgcolor: "transparent",
          minHeight: 0,

          ".MuiAccordionSummary-content": {
            my: 1,

            ".MuiTypography-body1": {
              fontSize: 12,
            },
          },
        },
        ".MuiAccordionDetails-root": {
          pt: 0,
          pb: 1,
          px: 0,
          color: "#9494AE",
          bgcolor: "transparent",
          fontSize: 9,

          "& .MuiTypography-root": {
            letterSpacing: 0.5,
            fontWeight: 700,
            textTransform: "uppercase",
            fontSize: "inherit",
          },

          "& .MuiSelect-select": {
            py: 0.5,
            px: 1,
          },

          "& .Side": {
            width: "100%",
            maxWidth: 170,
            fontSize: 9,
          },

          "& .MuiInputBase-root": {
            color: "white",
            bgcolor: "#222132",
            fontSize: 12,
            overflow: "hidden",
            borderRadius: 1,

            "&.MuiInputBase-multiline": {
              p: 0,
            },

            "input, & .MuiInputBase-inputMultiline, & .MuiInputBase-input": {
              p: 0,
              pl: 1,
              border: 0,
            },

            ".MuiButtonBase-root": {
              minWidth: 50,
              borderRadius: 0.5,

              // p: 0,
              bgcolor: "rgba(0,0,0,0.5)",
              fontSize: 10,
              lineHeight: "1.2em",
              height: 25,

              ".MuiOutlinedInput-notchedOutline": {
                border: 0,
              },

              ".MuiSelect-select": {
                p: 0.25,
                textAlign: "center",
              },

              ".MuiSelect-icon": {
                display: "none",
              },
            },
          },
          ".MuiInputBase-adornedEnd": {
            pr: 0,
            // py: 0.5,
          },
        },
      }}
    >
      {titles.map((title) => {
        const section: TXGUIField[] = settings[title];

        const isOne = titles.length === 1;

        return (
          <Accordion
            key={selectedElement.index + title}
            expanded={isOne || expanded === title}
            onChange={handleChange(title)}
            disableGutters
            sx={{ pt: isOne ? 2 : 0 }}
          >
            {!isOne && (
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`${title}-content`}
                id={`${title}-header`}
              >
                <Typography sx={{ flexShrink: 0 }}>{title}</Typography>
              </AccordionSummary>
            )}
            <AccordionDetails>
              {section.map((field) =>
                generateXGUIField({
                  field,
                  onChange,
                  currentValue: value[field.name],
                  index: selectedElement.index ?? "",
                  visible: isOne || expanded === title,
                })
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};

export default XGUIDesignTab;

const generateXGUIField = (args: IXGUIFieldBase) => {
  if (!args.visible) return null;

  const key = args.index + args.field.name;

  switch (args.field.type) {
    case "text":
    case "number":
      return (
        <React.Suspense key={key}>
          <XGUITextField {...args} type={args.field.type} />
        </React.Suspense>
      );

    case "sizing":
      return (
        <React.Suspense key={key}>
          <XGUISizingField {...args} />
        </React.Suspense>
      );
    case "color":
      return (
        <React.Suspense key={key}>
          <XGUIColorField {...args} />
        </React.Suspense>
      );
    case "select_option":
      return (
        <React.Suspense key={key}>
          <XGUISelectOptionField {...args} options={args.field.options ?? []} />
        </React.Suspense>
      );
    case "toggle_option":
      return (
        <React.Suspense key={key}>
          <XGUIToggleOptionField {...args} options={args.field.options ?? []} />
        </React.Suspense>
      );
    case "font":
    case "icon":
      return (
        <React.Suspense key={key}>
          <XGUIFontField {...args} />
        </React.Suspense>
      );
    case "sides":
      return (
        <React.Suspense key={key}>
          <XGUISideField {...args} />
        </React.Suspense>
      );
    case "radius":
      return (
        <React.Suspense key={key}>
          <XGUIRadiusField {...args} />
        </React.Suspense>
      );
    case "border":
      return (
        <React.Suspense key={key}>
          <XGUIBorderField {...args} />
        </React.Suspense>
      );
    case "boxShadow":
      return (
        <React.Suspense key={key}>
          <XGUIBoxShadowField {...args} />
        </React.Suspense>
      );
    case "image":
      return (
        <React.Suspense key={key}>
          <XGUIFilePicker {...args} type="image" />
        </React.Suspense>
      );
    default:
      return null;
  }
};
